/* eslint-disable func-names */
/* eslint-disable no-console */
/* eslint-disable no-multi-spaces */
/* eslint-disable one-var */

/**
 * @fileOverview  Design System JS launcher.
 * @author Selectra Front End Team
 * @version 1.0.0
 */

/**
 * @function appLoader
 * @description Test browser features, then inject a polyfill bundle that covers
 *     the unsupported features. Once it has loaded, execute application code.
 * @arg {Object} init - Initialization object
 * @arg {Function} init.appCode - Application code callback.
 * @arg {Array} [init.appRequiredFeatures] - Array of features present on the
 *     application code.
 * @arg {Object} [init.polyfillsUrls] - An object containing the paths for the
 *     polyfill bundles.
 */
export default function appLoader( init ) {

  // Check for mandatory parameters.
  if ( typeof init.appCode !== 'function' ) {
    console.error( 'You must pass an application function to execute.' );
    return null;
  }

  const

    // Application code callback.
    appCode = init.appCode,

    // Array of features required by the application code.
    appRequiredFeatures = init.appRequiredFeatures || [],

    // An object containing the paths for the polyfill bundles.
    polyfillUrls =  init.polyfillUrls || {
      full: '/js/polyfill/polyfill-full.min.js',
      partial: '/js/polyfill/polyfill-partial.min.js',
    },

    // Feature data
    features = {

      // Number
      number: {
        isInteger: {
          isSupported: 'isInteger' in Number,
          flag: 1,
        },
        parseInt: {
          isSupported: 'parseInt' in Number,
          flag: 1 << 1,
        },
        parseFloat: {
          isSupported: 'parseFloat' in Number,
          flag: 1 << 2,
        }
      },

      // Array
      array: {
        from: {
          isSupported: 'from' in Array,
          flag: 1 << 3,
        },
        forEach: {
          isSupported: 'forEach' in Array.prototype,
          flag: 1 << 4,
        },
        includes: {
          isSupported: 'includes' in Array.prototype,
          flag: 1 << 5,
        },
        slice: {
          isSupported: 'slice' in Array.prototype,
          flag: 1 << 6,
        }
      },

      // Math
      math: {
        sign: {
          isSupported: 'sign' in Math,
          flag: 1 << 7,
        },
      },

      // String
      string: {
        fromCodePoint: {
          isSupported: 'fromCodePoint' in String,
          flag: 1 << 8,
        },
        codePointAt: {
          isSupported: 'codePointAt' in String.prototype,
          flag: 1 << 9,
        },
        includes: {
          isSupported: 'includes' in String.prototype,
          flag: 1 << 10,
        },
        repeat: {
          isSupported: 'repeat' in String.prototype,
          flag: 1 << 11,
        },
      },

      // Object
      object: {
        assign: {
          isSupported: 'assign' in Object,
          flag: 1 << 12,
        }
      },

      // Symbol
      symbol: {
        all: {
          isSupported: 'Symbol' in window,
          flag: 1 << 13,
        }
      },

      // Promise
      promise: {
        all: {
          isSupported: typeof Promise === 'function',
          flag: 1 << 14,
        },
      },

      // ParentNode
      parentNode: {
        append: {
          isSupported:
            'append' in Element.prototype &&
            'append' in Document.prototype &&
            'append' in DocumentFragment.prototype,
          flag: 1 << 15,
        },
      },

      // Element
      element: {
        closest: {
          isSupported: 'closest' in Element.prototype,
          flag: 1 << 16,
        },
        matches: {
          isSupported: 'matches' in Element.prototype,
          flag: 1 << 17,
        },
        classList: {
          isSupported: 'classList' in Element.prototype,
          flag: 1 << 18,
        },
      },

      // NodeList
      nodeList: {
        forEach: {
          isSupported: 'forEach' in NodeList.prototype,
          flag: 1 << 19,
        },
      },

      // CustomEvent
      customEvent: {
        all: {
          isSupported: 'CustomEvent' in window,
          flag: 1 << 20,
        },
      },

      // Scroll API
      scroll: {
        all: {
          isSupported: 'scroll' in window,
          flag: 1 << 21,
        },
      },

      // IntersectionObserver API
      intersectionObserver: {
        all: {
          isSupported:
            'IntersectionObserver' in window &&
            'IntersectionObserverEntry' in window &&
            'intersectionRatio' in window.IntersectionObserverEntry.prototype,
          flag: 1 << 22,
        },
      },

      // Fetch
      fetch: {
        all: {
          isSupported: 'fetch' in window,
          flag: 1 << 23,
        },
      },

      // Position: sticky
      positionSticky: {
        all: {
          isSupported: 'scrollBehavior' in document.documentElement.style,
          flag: 1 << 24,
        },
      },
    };


  // Populate feature flag-sets
  let unsupportedFeats =
    ( function() {
      let unsupported = [];
      for ( let featureName in features ) {
        const feature = features[ featureName ];
        for ( let propertyName in feature ) {
          const
            property = feature[ propertyName ],
            propertyFlag = property.flag;
          if ( !property.isSupported ) {
            unsupported.push( propertyFlag );
          }
        }
      }
      return unsupported;
    }());


  const

    // The flags for features included in the partial polyfill bundle.
    partialFeaturesFlags = [
      features.scroll.all.flag,
      features.intersectionObserver.all.flag,
      features.fetch.all.flag,
      features.positionSticky.all.flag
    ];


  // Ditch the unsupported features *not required* by application code.
  if ( unsupportedFeats.length && appRequiredFeatures.length ) {

    let requiredFeaturesMask = 0;

    appRequiredFeatures.forEach( function( requiredFeature ) {
      const
        feature = requiredFeature[ 0 ],
        subFeatures = requiredFeature[ 1 ];
      subFeatures.forEach( function( subFeature ) {
        requiredFeaturesMask |= features[ feature ][ subFeature ].flag;
      })
    });

    unsupportedFeats =
      unsupportedFeats.filter( function( featureFlag ) {
        return (
          ( featureFlag & requiredFeaturesMask ) === featureFlag
        )
      });
  }


  // Conditionally inject polyfill bundle / execute application code.
  if ( unsupportedFeats.length === 0 ) {
    console.log( 'No polyfills needed.' );
    processAppCode();
  } else if ( checkUnsupportedFeaturesInBundle( partialFeaturesFlags )) {
    console.log( 'Loading partial polyfill bundle.' );
    injectScriptWithCallback( polyfillUrls.partial, processAppCode );
  } else {
    console.log( 'Loading full polyfill bundle.' );
    injectScriptWithCallback( polyfillUrls.full, processAppCode );
  }


  /**
   * @function checkUnsupportedFeaturesInBundle
   * @description Checks if the unsupported features are polyfilled by a
   *     polyfill bundle.
   * @arg {Array} polyBundleFlags - Array of unsupported features flags.
   */
  function checkUnsupportedFeaturesInBundle( polyBundleFlags ) {
    const
      totalUnsupportedFeatures = unsupportedFeats.length,
      polyBundleMask =
        polyBundleFlags.reduce( function( accumulator, flag ) {
          return accumulator | flag;
        });
    let allFeaturesInBundle = true;
    for ( let feature = 0; feature < totalUnsupportedFeatures; ++feature ) {
      const featureFlag = unsupportedFeats[ feature ];
      if (( featureFlag & polyBundleMask ) !== featureFlag ) {
        allFeaturesInBundle = false;
        break;
      }
    }
    return allFeaturesInBundle;
  }


  /**
   * @function processAppCode
   * @description Sets application code function to be called back on the
   *     'DOMContentLoaded' event. In the case it has already been fired,
   *     executes the code right ahead.
   */
  function processAppCode() {

    const executionCallback = function( event ) {
      appCode();
      event.currentTarget
        .removeEventListener( event.type, executionCallback );
    };

    document.readyState === 'loading' ?
      window.addEventListener( 'DOMContentLoaded', executionCallback ) :
      appCode();

  }


  /**
   * @function injectScriptWithCallBack
   * @description Browser agnostic solution to inject a script in the DOM and
   *     execute a function once it has finished loading.
   *
   * @arg {String} scriptUrl - URL for the script we want to inject.
   * @arg {Function} callback - The name of the function we want to call back
   *     once the script has finished loading.
   */
  function injectScriptWithCallback( scriptUrl, callback ) {

    const
      head =
        document.getElementsByTagName( 'head' )[ 0 ] ||
        document.documentElement,
      script = document.createElement( 'script' );

    let done = false;

    script.src = scriptUrl;

    script.onload = script.onreadystatechange = function() {
      if ( !done &&
          ( !this.readyState ||
            this.readyState === 'loaded' ||
            this.readyState === 'complete' )) {
        done = true;
        callback();

        script.onload = script.onreadystatechange = null;
        head.removeChild( script );
      }
    };

    head.insertBefore( script, head.firstChild );

  }

}
